import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, Archive, ArchiveRestore, ChevronDown, ChevronLeft, ChevronRight, Copy,
  Download, Euro, FileText, Landmark, Lock, MoreHorizontal, Paperclip, Pencil, Plus,
  Search, Send, SlidersHorizontal, Trash2, TrendingUp, Upload, Wallet, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur, INVOICE_STATUS, QUOTE_EDITABLE_STATUSES, QUOTE_STATUS, quoteNumber } from "@/components/accounting/docUtils";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend, PieChart, Pie, Cell } from "recharts";
import { SafeChart } from "@/components/SafeChart";
import InvoiceModal from "@/components/accounting/InvoiceModal";
import InvoiceDetail from "@/components/accounting/InvoiceDetail";
import SendDocModal from "@/components/accounting/SendDocModal";
import QuoteCreateModal from "@/components/accounting/QuoteCreateModal";
import QuoteDetailDrawer from "@/components/accounting/QuoteDetailDrawer";
import QuoteImportModal from "@/components/accounting/QuoteImportModal";
import RentabiliteTab from "@/components/accounting/RentabiliteTab";
import AutoBillingPanel from "@/components/accounting/AutoBillingPanel";
import PilotageGlobal from "@/components/accounting/pilotage/PilotageGlobal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const AVATAR_BG = ["bg-[#DBEAFE] text-[#1D4ED8]", "bg-[#FEF3C7] text-[#B45309]", "bg-[#EDE9FE] text-[#6D28D9]", "bg-[#DCFCE7] text-[#15803D]", "bg-[#FCE7F3] text-[#BE185D]"];
const avatarCls = (name = "") => AVATAR_BG[(name.charCodeAt(0) || 0) % AVATAR_BG.length];
const relTime = (iso) => {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d < 1) return "aujourd'hui";
  if (d === 1) return "il y a 1 jour";
  if (d < 7) return `il y a ${d} jours`;
  if (d < 14) return "il y a 1 semaine";
  return `il y a ${Math.floor(d / 7)} semaines`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; }
};
const quoteDesc = (q) => q.title || (q.items || [])[0]?.prestation || "—";
const invDesc = (inv) => inv.title || (inv.items || [])[0]?.prestation || "—";
const invEcheance = (inv) => {
  if (!inv.issue_date) return null;
  const d = new Date(inv.issue_date.slice(0, 10) + "T12:00:00");
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};
const SERVICE_COLORS = { web: "#8B5CF6", community: "#3B82F6", graphisme: "#F59E0B", autres: "#D1D5DB" };
const OVERDUE_BADGE = { label: "En retard", cls: "bg-rose-50 text-rose-600 border-rose-200" };

const TABS = [["devis", "Devis"], ["factures", "Factures"], ["auto", "Facturation auto"], ["avoirs", "Avoirs"], ["dashboard", "Pilotage"], ["pilotage", "Pilotage global"], ["rentabilite", "Rentabilité"]];

function AccTabs({ tab, setTab, children }) {
  return (
    <div className="flex items-center gap-5 border-b border-neutral-100 shrink-0 overflow-x-auto" data-testid="accounting-tabs">
      {TABS.map(([v, l]) => (
        <button key={v} onClick={() => setTab(v)} data-testid={`acc-tab-${v}`}
          className={`py-2.5 text-xs font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
            tab === v ? "border-[#1D4ED8] text-[#1D4ED8]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
          {l}
        </button>
      ))}
      {children}
    </div>
  );
}

function useEscape(open, close) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]); // eslint-disable-line
}

function RowMenu({ items, testid }) {
  const [open, setOpen] = useState(false);
  useEscape(open, () => setOpen(false));
  const visible = items.filter(Boolean);
  if (!visible.length) return <span className="w-6" />;
  return (
    <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button data-testid={testid} onClick={() => setOpen((v) => !v)}
        className="p-1 rounded-md text-neutral-300 hover:text-neutral-700 hover:bg-neutral-100 transition-colors">
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-6 z-40 w-56 bg-white border border-neutral-200 rounded-xl shadow-lg py-1">
            {visible.map((it) => (
              <button key={it.label} onClick={() => { setOpen(false); it.onClick(); }} data-testid={it.testid}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${it.danger ? "text-red-600 hover:bg-red-50" : "text-neutral-600 hover:bg-neutral-50"}`}>
                {it.icon}{it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NewCreditNoteModal({ open, onClose, invoices, onSaved }) {
  const [invId, setInvId] = useState("");
  const [total, setTotal] = useState(true);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const eligible = invoices.filter((i) => i.number);
  useEffect(() => { if (open) { setInvId(""); setTotal(true); setAmount(""); setReason(""); } }, [open]);
  const submit = async () => {
    if (!invId) { toast.error("Sélectionnez une facture"); return; }
    setBusy(true);
    try {
      await api.post(`/accounting/invoices/${invId}/credit-notes`, {
        total, amount: total ? undefined : Number(amount) || 0, reason,
      });
      toast.success("Avoir créé");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="new-cn-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Nouvel avoir</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Facture concernée *</Label>
            <select className="mt-1 w-full text-sm border border-neutral-200 rounded-lg px-2.5 py-2 bg-white" value={invId}
              onChange={(e) => setInvId(e.target.value)} data-testid="cn-invoice">
              <option value="">Choisir…</option>
              {eligible.map((i) => <option key={i.id} value={i.id}>{i.number} — {i.client_name} ({eur(i.total_ttc)})</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="cn-total-toggle">
            <input type="checkbox" className="rounded" checked={total} onChange={(e) => setTotal(e.target.checked)} />
            Avoir total (montant de la facture)
          </label>
          {!total && (
            <div>
              <Label className="text-xs">Montant de l'avoir (€ TTC)</Label>
              <Input type="number" min="0" className="mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cn-amount" />
            </div>
          )}
          <div>
            <Label className="text-xs">Motif</Label>
            <Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : geste commercial" data-testid="cn-reason" />
          </div>
          <Button onClick={submit} disabled={busy || !invId} className="w-full bg-[#0B132B] hover:bg-[#1C2A4F] text-white" data-testid="cn-submit">
            {busy ? "Création…" : "Créer l'avoir"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Accounting() {
  const [searchParams] = useSearchParams();
  const initialTab = TABS.some(([v]) => v === searchParams.get("tab")) ? searchParams.get("tab") : "devis";
  const [tab, setTab] = useState(initialTab);
  const [dash, setDash] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [yearSel, setYearSel] = useState(null);
  const [monthKey, setMonthKey] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [sendCn, setSendCn] = useState(null);
  const [cnModal, setCnModal] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [sendQuote, setSendQuote] = useState(null);
  const [quoteDetailId, setQuoteDetailId] = useState(null);
  const [editQuote, setEditQuote] = useState(null);
  const [quotesArchived, setQuotesArchived] = useState(false);
  const [importQuoteOpen, setImportQuoteOpen] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [archivedList, setArchivedList] = useState([]);
  const [reminding, setReminding] = useState(null);
  const [params] = useSearchParams();
  useEscape(newMenuOpen, () => setNewMenuOpen(false));
  useEscape(filtersOpen, () => setFiltersOpen(false));

  useEffect(() => {
    const t = params.get("tab");
    if (t) setTab(t);
    const inv = params.get("invoice");
    if (inv) { setDetailId(inv); setTab("factures"); }
    const qp = params.get("quote");
    if (qp) { setQuoteDetailId(qp); setTab("devis"); }
  }, [params]);

  const load = (annee) => {
    api.get(`/accounting/dashboard${annee ? `?annee=${annee}` : ""}`).then((r) => {
      setDash(r.data);
      if (!yearSel) setYearSel(r.data.sel_year?.annee);
    }).catch(() => {});
    api.get("/accounting/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get(quotesArchived ? "/quotes?archived=1" : "/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/accounting/credit-notes").then((r) => setCreditNotes(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data.filter((c) => !c.archived))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [quotesArchived]);
  useEffect(() => {
    setSelected(new Set());
    if (statusFilter === "archives") {
      api.get("/accounting/invoices?archived=true").then((r) => setArchivedList(r.data)).catch(() => {});
    }
  }, [statusFilter]);
  useEffect(() => { setPage(1); }, [tab, q, clientFilter, statusFilter, amountMin, amountMax, pageSize]);

  const changeYear = (y) => { setYearSel(y); setMonthKey(null); api.get(`/accounting/dashboard?annee=${y}`).then((r) => setDash(r.data)).catch(() => {}); };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) { n.delete(id); } else { n.add(id); }
    return n;
  });
  const bulkArchive = async (flag) => {
    try {
      const r = await api.post("/accounting/invoices/archive", { ids: [...selected], archived: flag });
      toast.success(flag ? `${r.data.count} facture(s) archivée(s) — conservées dans le dossier Archives` : `${r.data.count} facture(s) restaurée(s)`);
      setSelected(new Set());
      load();
      if (statusFilter === "archives") {
        const rr = await api.get("/accounting/invoices?archived=true");
        setArchivedList(rr.data);
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  const archiveInvoice = async (inv) => {
    try {
      await api.post("/accounting/invoices/archive", { ids: [inv.id], archived: true });
      toast.success("Facture archivée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remind = async (invoiceId) => {
    setReminding(invoiceId);
    try {
      await api.post(`/accounting/invoices/${invoiceId}/remind`);
      toast.success("Relance envoyée avec succès");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setReminding(null); }
  };
  const openCnPdf = async (cn) => {
    const r = await api.get(`/accounting/credit-notes/${cn.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
  };
  const duplicateQuote = async (qu) => {
    const r = await api.post(`/accounting/quotes/${qu.id}/duplicate`);
    toast.success(`Devis N° ${r.data.number} créé par duplication`);
    load();
  };
  const depositInvoice = async (qu) => {
    try {
      const r = await api.post(`/accounting/quotes/${qu.id}/deposit-invoice`);
      toast.success("Facture d'acompte créée en brouillon");
      load(); setTab("factures"); setDetailId(r.data.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const balanceInvoice = async (qu) => {
    try {
      const r = await api.post(`/accounting/quotes/${qu.id}/balance-invoice`);
      toast.success("Facture de solde créée en brouillon");
      load(); setTab("factures"); setDetailId(r.data.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const duplicateInvoice = async (inv) => {
    const r = await api.post(`/accounting/invoices/${inv.id}/duplicate`);
    toast.success("Facture dupliquée en brouillon (nouveau numéro à la validation)");
    load();
  };
  const openQuotePdf = async (qu) => {
    const r = await api.get(`/accounting/quotes/${qu.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
  };
  const openInvPdf = async (inv) => {
    const r = await api.get(`/accounting/invoices/${inv.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
  };
  const deleteQuote = async (qu) => {
    try {
      await api.delete(`/accounting/quotes/${qu.id}`);
      toast.success("Brouillon de devis supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const archiveQuote = async (qu) => {
    try {
      await api.post(`/accounting/quotes/${qu.id}/archive`, { archived: true });
      toast.success("Devis archivé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openImportPdf = async (qu) => {
    try {
      const r = await api.get(`/accounting/quotes/${qu.id}/attachment`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("Impossible d'ouvrir la pièce jointe"); }
  };
  const acompteLabel = (qu) => {
    if (!qu.acompte_type || qu.acompte_type === "aucun") return null;
    const amount = qu.acompte_type === "pourcent" ? (qu.total_ttc * (qu.acompte_value || 0)) / 100 : qu.acompte_value || 0;
    return `Acompte ${qu.acompte_type === "pourcent" ? `${qu.acompte_value} %` : "fixe"} : ${eur(amount)}`;
  };

  /* ---------- Données dérivées ---------- */
  const overdueIds = useMemo(() => new Set((dash?.a_relancer || []).map((x) => x.id)), [dash]);
  const invStatus = (inv) => (overdueIds.has(inv.id) ? OVERDUE_BADGE : (INVOICE_STATUS[inv.status] || INVOICE_STATUS.brouillon));

  const matchDoc = (d, numField, descFn, totalField) => {
    if (clientFilter && d.client_id !== clientFilter) return false;
    if (amountMin && Number(d[totalField]) < Number(amountMin)) return false;
    if (amountMax && Number(d[totalField]) > Number(amountMax)) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = [d.number, d.original_number, d.client_name, descFn(d), String(d[totalField] || "")]
        .map((s) => (s || "").toString().toLowerCase());
      if (!hay.some((s) => s.includes(needle))) return false;
    }
    return true;
  };

  const quotesShown = useMemo(() => quotes
    .filter((d) => (!statusFilter || d.status === statusFilter) && matchDoc(d, "number", quoteDesc, "total_ttc")),
  [quotes, statusFilter, clientFilter, amountMin, amountMax, q]); // eslint-disable-line

  const invoicesShown = useMemo(() => {
    let list = statusFilter === "archives" ? archivedList : invoices;
    return list.filter((d) => {
      if (statusFilter === "en_retard" && !overdueIds.has(d.id)) return false;
      if (statusFilter && statusFilter !== "archives" && statusFilter !== "en_retard" && d.status !== statusFilter) return false;
      return matchDoc(d, "number", invDesc, "total_ttc");
    });
  }, [invoices, archivedList, statusFilter, clientFilter, amountMin, amountMax, q, overdueIds]); // eslint-disable-line

  const cnShown = useMemo(() => creditNotes
    .filter((d) => matchDoc(d, "number", (c) => c.reason || `Facture ${c.invoice_number}`, "amount_ttc")),
  [creditNotes, clientFilter, amountMin, amountMax, q]); // eslint-disable-line

  const rows = tab === "devis" ? quotesShown : tab === "factures" ? invoicesShown : cnShown;
  const totalRows = rows.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  /* ---------- KPI ---------- */
  const sy = dash?.sel_year;
  const months = sy?.months || [];
  const effMonthKey = monthKey || (sy
    ? (sy.annee === dash.annee ? `${dash.annee}-${String(new Date().getMonth() + 1).padStart(2, "0")}` : `${sy.annee}-12`)
    : null);
  const monthIdx = months.findIndex((m) => m.key === effMonthKey);
  const caMois = monthIdx >= 0 ? months[monthIdx].encaisse : 0;
  const caPrev = monthIdx > 0 ? months[monthIdx - 1].encaisse : 0;
  const varMois = caPrev > 0 ? Math.round((caMois - caPrev) / caPrev * 100) : null;
  const varYear = sy && sy.encaisse_prev > 0 ? Math.round((sy.encaisse - sy.encaisse_prev) / sy.encaisse_prev * 100) : null;
  const monthLabel = (k) => {
    const m = months.find((x) => x.key === k);
    return m ? `${m.label} ${k.slice(0, 4)}` : k;
  };
  const repServices = dash?.repartition_services || [];
  const repTotal = repServices.reduce((a, s) => a + s.amount, 0);
  const activeFilters = [clientFilter, statusFilter, amountMin, amountMax].filter(Boolean).length;

  const kpis = [
    { id: "ca-mois", label: "CA du mois", icon: TrendingUp, bg: "#E8F0FE", fg: "#1D4ED8",
      value: eur(caMois), monthPicker: true,
      sub: varMois != null ? `${varMois >= 0 ? "↑ +" : "↓ "}${varMois} %` : "—", subLbl: `vs ${monthIdx > 0 ? months[monthIdx - 1].label : "—"}`, subCls: varMois != null && varMois < 0 ? "text-rose-500" : "text-emerald-600" },
    { id: "devis-attente", label: "Devis en attente", icon: FileText, bg: "#FEF3E2", fg: "#B45309",
      value: eur(dash?.devis_attente?.total || 0), sub: `${dash?.devis_attente?.count ?? 0} devis`, subLbl: "", subCls: "text-neutral-400" },
    { id: "a-encaisser", label: "Factures à encaisser", icon: Wallet, bg: "#DCFCE7", fg: "#15803D",
      value: eur(dash?.a_encaisser?.total || 0), sub: `${dash?.a_encaisser?.count ?? 0} factures`, subLbl: "", subCls: "text-neutral-400" },
    { id: "impayes", label: "Impayés", icon: AlertTriangle, bg: "#FDEAE8", fg: "#B91C1C",
      value: eur(dash?.impayes?.total || 0), sub: `${dash?.impayes?.count ?? 0} facture(s) en retard`, subLbl: "", subCls: "text-neutral-400" },
  ];

  const newBtn = tab === "devis"
    ? { label: "Nouveau devis", testid: "new-quote-btn", fn: () => setQuoteModalOpen(true) }
    : tab === "factures"
      ? { label: "Nouvelle facture", testid: "new-invoice-btn", fn: () => { setEditing(null); setModalOpen(true); } }
      : { label: "Nouvel avoir", testid: "new-cn-btn", fn: () => setCnModal(true) };

  const statusOptions = tab === "devis"
    ? Object.entries(QUOTE_STATUS).map(([id, s]) => [id, s.label])
    : tab === "factures"
      ? [...Object.entries(INVOICE_STATUS).map(([id, s]) => [id, s.label]), ["en_retard", "En retard"], ["archives", "Archives"]]
      : [];

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-7.5rem)] overflow-hidden" data-testid="accounting-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div>
          {tab === "rentabilite" ? (
            <>
              <h1 className="font-heading text-2xl font-bold flex items-center gap-2" data-testid="rentabilite-title">Rentabilité <TrendingUp className="w-5 h-5 text-[#1D4ED8]" /></h1>
              <p className="text-neutral-500 text-xs mt-0.5">Analysez la rentabilité de l'agence par client en fonction du budget facturé et du temps passé.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold flex items-center gap-2">Comptabilité <Landmark className="w-5 h-5 text-[#1D4ED8]" /></h1>
              <p className="text-neutral-500 text-xs mt-0.5">Suivez vos devis, factures et paiements en toute simplicité.</p>
            </>
          )}
        </div>
        <p className="hidden 2xl:block text-right text-xs italic text-neutral-400 leading-snug">« Une bonne gestion aujourd'hui,<br />une agence plus forte demain. »</p>
      </div>

      {/* KPI */}
      {tab !== "rentabilite" && tab !== "pilotage" && tab !== "auto" && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0" data-testid="acc-kpis">
        {kpis.map((c) => (
          <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5" data-testid={`kpi-${c.id}`}>
            <div className="flex items-center gap-2.5">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: c.bg }}>
                <c.icon className="w-4.5 h-4.5" style={{ color: c.fg }} />
              </span>
              <p className="text-[11px] font-medium text-neutral-500 leading-tight flex-1">{c.label}</p>
              {c.monthPicker && sy && (
                <select value={effMonthKey || ""} onChange={(e) => setMonthKey(e.target.value)} data-testid="kpi-month-select"
                  className="text-[10px] font-semibold text-neutral-500 border border-neutral-200 rounded-lg px-1.5 py-1 bg-white focus:outline-none cursor-pointer">
                  {months.map((m) => <option key={m.key} value={m.key}>{monthLabel(m.key)}</option>)}
                </select>
              )}
            </div>
            <p className="font-heading text-2xl font-bold mt-1.5 leading-tight" data-testid={`kpi-${c.id}-value`}>{c.value}</p>
            <p className="text-[10px] mt-0.5"><span className={`font-semibold ${c.subCls}`}>{c.sub}</span> <span className="text-neutral-400">{c.subLbl}</span></p>
          </div>
        ))}
      </div>
      )}

      {tab === "auto" ? (
        /* ============ FACTURATION AUTO (cycle mensuel) ============ */
        <div className="flex-1 min-h-0 overflow-y-auto pr-0.5">
          <div className="mb-4 shrink-0"><AccTabs tab={tab} setTab={setTab} /></div>
          <AutoBillingPanel variant="full" onOpenInvoice={(id) => { setTab("factures"); setDetailId(id); }} />
        </div>
      ) : tab === "pilotage" ? (
        /* ============ PILOTAGE GLOBAL (module financier freelance) ============ */
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="mb-0 shrink-0"><AccTabs tab={tab} setTab={setTab} /></div>
          <PilotageGlobal />
        </div>
      ) : tab === "dashboard" ? (
        /* ============ PILOTAGE (ancien tableau de bord, conservé à l'identique) ============ */
        <div className="flex-1 min-h-0 overflow-y-auto pr-0.5">
          <div className="mb-4 shrink-0"><AccTabs tab={tab} setTab={setTab} /></div>
          {dash && <Pilotage dash={dash} onOpenInvoice={setDetailId} remind={remind} reminding={reminding} onSeeAllAuto={() => setTab("auto")} />}
        </div>
      ) : tab === "rentabilite" ? (
        /* ============ RENTABILITÉ (seuil central : Paramètres → Rentabilité) ============ */
        <div className="flex-1 min-h-0 flex flex-col overflow-y-auto pr-0.5">
          <div className="shrink-0"><AccTabs tab={tab} setTab={setTab} /></div>
          <RentabiliteTab />
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_330px] gap-3">
          {/* ============ Bloc principal : onglets + recherche + tableau ============ */}
          <div className="min-h-0 flex flex-col bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="acc-main">
            <div className="px-4 pt-1 shrink-0">
              <AccTabs tab={tab} setTab={setTab}>
              <div className="ml-auto flex items-center gap-2 py-1.5">
                {tab === "devis" && (
                  <button onClick={() => setQuotesArchived((v) => !v)} data-testid="quotes-archives-toggle"
                    className="text-[11px] font-semibold text-neutral-400 hover:text-neutral-700 transition-colors">
                    {quotesArchived ? "← Devis actifs" : "Archives"}
                  </button>
                )}
                <div className="relative flex items-center">
                  <Button size="sm" onClick={newBtn.fn} className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5 rounded-r-none" data-testid={newBtn.testid}>
                    <Plus className="w-3.5 h-3.5" /> {newBtn.label}
                  </Button>
                  <button onClick={() => setNewMenuOpen((v) => !v)} data-testid="acc-new-menu-btn"
                    className="h-8 px-1.5 bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-r-md border-l border-white/20 transition-colors">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {newMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setNewMenuOpen(false)} />
                      <div className="absolute right-0 top-9 z-40 w-56 bg-white border border-neutral-200 rounded-xl shadow-lg py-1" data-testid="acc-new-menu">
                        {tab === "devis" && (
                          <button onClick={() => { setNewMenuOpen(false); setImportQuoteOpen(true); }} data-testid="import-quote-btn"
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50">
                            <Upload className="w-3.5 h-3.5 text-neutral-400" /> Importer un devis PDF
                          </button>
                        )}
                        {tab === "factures" && (
                          <button onClick={() => { setNewMenuOpen(false); setStatusFilter("archives"); }} data-testid="acc-see-archives"
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50">
                            <Archive className="w-3.5 h-3.5 text-neutral-400" /> Voir les archives
                          </button>
                        )}
                        {tab === "avoirs" && (
                          <p className="px-3 py-2 text-[11px] text-neutral-400">Un avoir est toujours lié à une facture définitive.</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              </AccTabs>
            </div>

            {tab === "factures" && (
              <div className="px-4 pt-2 shrink-0">
                <AutoBillingPanel variant="compact" onSeeAll={() => setTab("auto")} />
              </div>
            )}

            {/* Recherche + filtres */}
            <div className="flex items-center gap-2 px-4 py-2.5 shrink-0">
              <div className="relative flex-1 max-w-md">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input className="pl-8 h-8 text-xs rounded-lg" placeholder="Rechercher un numéro, un client, un projet…" value={q}
                  onChange={(e) => setQ(e.target.value)} data-testid="acc-search" />
              </div>
              <div className="relative ml-auto">
                <button onClick={() => setFiltersOpen((v) => !v)} data-testid="acc-filters-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 border border-neutral-200 rounded-lg px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Filtres
                  {activeFilters > 0 && <span className="w-4 h-4 rounded-full bg-[#0B132B] text-white text-[9px] font-bold flex items-center justify-center">{activeFilters}</span>}
                </button>
                {filtersOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setFiltersOpen(false)} />
                    <div className="absolute right-0 top-9 z-40 w-64 bg-white border border-neutral-200 rounded-xl shadow-lg p-3 space-y-2" data-testid="acc-filters-pop">
                      <select className="w-full border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs bg-white" value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)} data-testid="invoice-client-filter">
                        <option value="">Tous les clients</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      {statusOptions.length > 0 && (
                        <select className="w-full border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs bg-white" value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)} data-testid="acc-filter-status">
                          <option value="">Tous les statuts</option>
                          {statusOptions.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
                        </select>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="number" placeholder="Montant min" className="h-8 text-xs" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} data-testid="acc-filter-min" />
                        <Input type="number" placeholder="Montant max" className="h-8 text-xs" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} data-testid="acc-filter-max" />
                      </div>
                      {activeFilters > 0 && (
                        <button onClick={() => { setClientFilter(""); setStatusFilter(""); setAmountMin(""); setAmountMax(""); }}
                          className="w-full text-[11px] text-[#0369A1] hover:underline" data-testid="acc-filters-reset">
                          Réinitialiser les filtres
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Barre de sélection (factures) */}
            {tab === "factures" && selected.size > 0 && (
              <div className="flex items-center gap-3 bg-neutral-900 text-white rounded-xl px-4 py-2 mx-4 mb-2 text-xs shrink-0" data-testid="selection-bar">
                <span className="font-medium">{selected.size} facture(s) sélectionnée(s)</span>
                <span className="flex-1" />
                {statusFilter === "archives" ? (
                  <button onClick={() => bulkArchive(false)} data-testid="restore-selected-btn"
                    className="flex items-center gap-1.5 bg-white text-neutral-900 rounded-full px-3 py-1 text-xs font-medium hover:bg-neutral-100 transition-colors">
                    <ArchiveRestore className="w-3.5 h-3.5" /> Restaurer
                  </button>
                ) : (
                  <button onClick={() => bulkArchive(true)} data-testid="archive-selected-btn"
                    className="flex items-center gap-1.5 bg-white text-neutral-900 rounded-full px-3 py-1 text-xs font-medium hover:bg-neutral-100 transition-colors">
                    <Archive className="w-3.5 h-3.5" /> Archiver
                  </button>
                )}
                <button onClick={() => setSelected(new Set())} data-testid="clear-selection-btn"
                  className="text-xs text-white/60 hover:text-white transition-colors">Tout désélectionner</button>
              </div>
            )}

            {/* Tableau */}
            <div className="flex-1 min-h-0 overflow-auto px-2" data-testid="acc-table-wrap">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-400 border-b border-neutral-100">
                    <th className="px-2 py-2 w-8">
                      {tab === "factures" && <span className="sr-only">Sélection</span>}
                    </th>
                    <th className="px-2 py-2 font-semibold">N°</th>
                    <th className="px-2 py-2 font-semibold">Client</th>
                    <th className="px-2 py-2 font-semibold">Projet / Description</th>
                    <th className="px-2 py-2 font-semibold text-right">Montant</th>
                    <th className="px-2 py-2 font-semibold">Date</th>
                    <th className="px-2 py-2 font-semibold">Statut</th>
                    <th className="px-2 py-2 font-semibold">Échéance</th>
                    <th className="px-2 py-2 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {totalRows === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-neutral-400" data-testid="acc-empty">
                      {tab === "devis" ? "Aucun devis pour ces critères." : tab === "factures" ? "Aucune facture dans cette catégorie." : "Aucun avoir."}
                    </td></tr>
                  )}
                  {tab === "devis" && pageRows.map((d) => {
                    const st = QUOTE_STATUS[d.status] || QUOTE_STATUS.envoye;
                    const aco = acompteLabel(d);
                    const locked = !QUOTE_EDITABLE_STATUSES.includes(d.status);
                    return (
                      <tr key={d.id} onClick={() => setQuoteDetailId(d.id)} data-testid={`quote-row-${d.id}`}
                        className="border-b border-neutral-50 last:border-0 hover:bg-neutral-50/70 cursor-pointer transition-colors">
                        <td className="px-2 py-2.5" />
                        <td className="px-2 py-2.5 font-semibold text-xs text-[#0F172A] whitespace-nowrap">
                          {quoteNumber(d)}
                          {locked && <Lock className="w-3 h-3 inline ml-1.5 text-neutral-300" title="Document verrouillé" />}
                        </td>
                        <td className="px-2 py-2.5">
                          <span className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0 ${avatarCls(d.client_name)}`}>{initialsOf(d.client_name)}</span>
                            <span className="text-xs font-medium text-neutral-700 truncate max-w-36">{d.client_name}</span>
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-xs text-neutral-500 truncate max-w-44">
                          {quoteDesc(d)}
                          {d.imported && <span className="ml-1.5 text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-px" data-testid={`quote-imported-${d.id}`}>Importé</span>}
                          {aco && <span className="ml-1.5 text-[9px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-px" data-testid={`quote-acompte-${d.id}`}>{aco}</span>}
                        </td>
                        <td className="px-2 py-2.5 text-xs font-semibold text-right whitespace-nowrap">{eur(d.total_ttc)}</td>
                        <td className="px-2 py-2.5 text-xs text-neutral-400 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                        <td className="px-2 py-2.5"><span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`quote-status-${d.id}`}>{st.label}</span></td>
                        <td className="px-2 py-2.5 text-xs text-neutral-400 whitespace-nowrap">{d.delay || "—"}</td>
                        <td className="px-2 py-2.5">
                          <RowMenu testid={`quote-menu-${d.id}`} items={[
                            { label: "Ouvrir", icon: <FileText className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => setQuoteDetailId(d.id), testid: `quote-open-${d.id}` },
                            !locked && { label: "Modifier", icon: <Pencil className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => setEditQuote(d), testid: `quote-edit-${d.id}` },
                            { label: "Télécharger PDF", icon: <Download className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => openQuotePdf(d), testid: `quote-pdf-${d.id}` },
                            !locked && { label: d.status === "brouillon" ? "Aperçu & envoi" : "Renvoyer", icon: <Send className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => setSendQuote(d), testid: `quote-send-${d.id}` },
                            d.import_pdf_path && { label: "PDF importé (original)", icon: <Paperclip className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => openImportPdf(d), testid: `quote-attachment-${d.id}` },
                            d.status === "accepte" && aco && !d.deposit_invoice_id && { label: "Facturer l'acompte", icon: <Euro className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => depositInvoice(d), testid: `quote-deposit-${d.id}` },
                            d.status === "accepte" && d.deposit_invoice_id && !d.balance_invoice_id && { label: "Facturer le solde", icon: <Euro className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => balanceInvoice(d), testid: `quote-balance-${d.id}` },
                            { label: "Dupliquer", icon: <Copy className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => duplicateQuote(d), testid: `quote-duplicate-${d.id}` },
                            d.status === "brouillon"
                              ? { label: "Supprimer le brouillon", icon: <Trash2 className="w-3.5 h-3.5" />, danger: true, onClick: () => deleteQuote(d), testid: `quote-delete-${d.id}` }
                              : { label: "Archiver", icon: <Archive className="w-3.5 h-3.5" />, onClick: () => archiveQuote(d), testid: `quote-archive-${d.id}` },
                          ]} />
                        </td>
                      </tr>
                    );
                  })}
                  {tab === "factures" && pageRows.map((inv) => {
                    const st = invStatus(inv);
                    const reste = Math.max(0, inv.total_ttc - (inv.paid_total || 0));
                    const locked = inv.status !== "brouillon";
                    return (
                      <tr key={inv.id} onClick={() => setDetailId(inv.id)} data-testid={`invoice-row-${inv.id}`}
                        className="border-b border-neutral-50 last:border-0 hover:bg-neutral-50/70 cursor-pointer transition-colors">
                        <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()} data-testid={`invoice-select-${inv.id}`}>
                          <input type="checkbox" checked={selected.has(inv.id)} onChange={() => toggleSelect(inv.id)}
                            className="w-3.5 h-3.5 accent-neutral-900 cursor-pointer" aria-label="Sélectionner la facture" />
                        </td>
                        <td className="px-2 py-2.5 font-semibold text-xs text-[#0F172A] whitespace-nowrap">
                          {inv.number || "Brouillon"}
                          {locked && <Lock className="w-3 h-3 inline ml-1.5 text-neutral-300" title="Document verrouillé" />}
                        </td>
                        <td className="px-2 py-2.5">
                          <span className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0 ${avatarCls(inv.client_name)}`}>{initialsOf(inv.client_name)}</span>
                            <span className="text-xs font-medium text-neutral-700 truncate max-w-36">{inv.client_name}</span>
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-xs text-neutral-500 truncate max-w-44">{invDesc(inv)}</td>
                        <td className="px-2 py-2.5 text-xs font-semibold text-right whitespace-nowrap">
                          {eur(inv.total_ttc)}
                          {reste > 0 && inv.status !== "brouillon" && <span className="block text-[9px] font-medium text-orange-600">reste {eur(reste)}</span>}
                        </td>
                        <td className="px-2 py-2.5 text-xs text-neutral-400 whitespace-nowrap">{fmtDate(inv.issue_date || inv.created_at)}</td>
                        <td className="px-2 py-2.5"><span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`invoice-status-${inv.id}`}>{st.label}</span></td>
                        <td className="px-2 py-2.5 text-xs text-neutral-400 whitespace-nowrap">{fmtDate(invEcheance(inv))}</td>
                        <td className="px-2 py-2.5">
                          <RowMenu testid={`invoice-menu-${inv.id}`} items={[
                            { label: "Ouvrir", icon: <FileText className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => setDetailId(inv.id), testid: `invoice-open-${inv.id}` },
                            { label: "Télécharger PDF", icon: <Download className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => openInvPdf(inv), testid: `invoice-pdf-${inv.id}` },
                            locked && { label: "Enregistrer un paiement", icon: <Euro className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => setDetailId(inv.id), testid: `invoice-pay-${inv.id}` },
                            overdueIds.has(inv.id) && { label: "Relancer", icon: <Send className="w-3.5 h-3.5 text-orange-500" />, onClick: () => { if (reminding !== inv.id) remind(inv.id); }, testid: `invoice-remind-${inv.id}` },
                            { label: "Créer un avoir", icon: <FileText className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => setCnModal(true), testid: `invoice-cn-${inv.id}` },
                            { label: "Dupliquer", icon: <Copy className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => duplicateInvoice(inv), testid: `invoice-duplicate-${inv.id}` },
                            { label: "Archiver", icon: <Archive className="w-3.5 h-3.5" />, onClick: () => archiveInvoice(inv), testid: `invoice-archive-${inv.id}` },
                          ]} />
                        </td>
                      </tr>
                    );
                  })}
                  {tab === "avoirs" && pageRows.map((cn) => (
                    <tr key={cn.id} onClick={() => openCnPdf(cn)} data-testid={`cn-row-${cn.id}`}
                      className="border-b border-neutral-50 last:border-0 hover:bg-neutral-50/70 cursor-pointer transition-colors">
                      <td className="px-2 py-2.5" />
                      <td className="px-2 py-2.5 font-semibold text-xs text-[#0F172A] whitespace-nowrap">{cn.number}</td>
                      <td className="px-2 py-2.5">
                        <span className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0 ${avatarCls(cn.client_name)}`}>{initialsOf(cn.client_name)}</span>
                          <span className="text-xs font-medium text-neutral-700 truncate max-w-36">{cn.client_name}</span>
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-xs text-neutral-500 truncate max-w-44">Facture {cn.invoice_number}{cn.reason ? ` — ${cn.reason}` : ""}</td>
                      <td className="px-2 py-2.5 text-xs font-semibold text-right whitespace-nowrap text-neutral-700">−{eur(cn.amount_ttc)}</td>
                      <td className="px-2 py-2.5 text-xs text-neutral-400 whitespace-nowrap">{fmtDate(cn.issue_date || cn.created_at)}</td>
                      <td className="px-2 py-2.5"><span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-neutral-100 text-neutral-500 border-neutral-200">Émis</span></td>
                      <td className="px-2 py-2.5 text-xs text-neutral-400">—</td>
                      <td className="px-2 py-2.5">
                        <RowMenu testid={`cn-menu-${cn.id}`} items={[
                          { label: "Télécharger PDF", icon: <Download className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => openCnPdf(cn), testid: `cn-pdf-${cn.id}` },
                          { label: "Envoyer", icon: <Send className="w-3.5 h-3.5 text-neutral-400" />, onClick: () => setSendCn(cn), testid: `cn-send-${cn.id}` },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-t border-neutral-100 shrink-0" data-testid="acc-pagination">
              <p className="text-[11px] text-neutral-400">Affichage de {pageRows.length} sur {totalRows} documents</p>
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="acc-page-prev"
                  className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 disabled:opacity-30 transition-colors">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
                  let pnum = pageCount <= 5 ? i + 1 : Math.min(Math.max(1, page - 2), pageCount - 4) + i;
                  return (
                    <button key={pnum} onClick={() => setPage(pnum)} data-testid={`acc-page-${pnum}`}
                      className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${page === pnum ? "bg-[#1D4ED8] text-white" : "text-neutral-500 hover:bg-neutral-100"}`}>
                      {pnum}
                    </button>
                  );
                })}
                <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount} data-testid="acc-page-next"
                  className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 disabled:opacity-30 transition-colors">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} data-testid="acc-pagesize"
                  className="ml-2 text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none cursor-pointer">
                  <option value={10}>10 / page</option><option value={25}>25 / page</option><option value={50}>50 / page</option>
                </select>
              </div>
            </div>
          </div>

          {/* ============ Colonne droite : stats ============ */}
          <div className="min-h-0 overflow-y-auto space-y-3 pr-0.5" data-testid="acc-sidebar">
            {/* Évolution du CA */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4" data-testid="acc-chart-year">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-neutral-400" />
                <h3 className="font-heading font-bold text-sm flex-1">Évolution du chiffre d'affaires</h3>
                {dash?.annees?.length > 0 && (
                  <select value={yearSel || dash.annee} onChange={(e) => changeYear(Number(e.target.value))} data-testid="acc-year-select"
                    className="text-[11px] font-semibold text-[#0369A1] border border-neutral-200 rounded-lg px-1.5 py-1 bg-white focus:outline-none cursor-pointer">
                    {dash.annees.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                )}
              </div>
              <p className="font-heading text-2xl font-bold" data-testid="acc-ca-annee">{eur(sy?.encaisse || 0)}
                {varYear != null && (
                  <span className={`ml-2 text-xs font-semibold ${varYear >= 0 ? "text-emerald-600" : "text-rose-500"}`} data-testid="acc-ca-var">
                    {varYear >= 0 ? "↑ +" : "↓ "}{varYear} %
                  </span>
                )}
              </p>
              <p className="text-[10px] text-neutral-400 mb-2">CA encaissé {sy?.annee}{varYear != null ? ` · vs même période en ${sy.annee - 1}` : ""}</p>
              <div className="h-36">
                <SafeChart width="100%" height="100%">
                  <BarChart data={months} margin={{ left: -28, right: 0, top: 5, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 8 }} interval={1} />
                    <YAxis tick={{ fontSize: 8 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Bar dataKey="encaisse" name="Encaissé" radius={[3, 3, 0, 0]}>
                      {months.map((m) => (
                        <Cell key={m.key} fill={m.key === effMonthKey ? "#1D4ED8" : "#BFDBFE"} />
                      ))}
                    </Bar>
                  </BarChart>
                </SafeChart>
              </div>
            </div>

            {/* Répartition du CA */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4" data-testid="acc-donut">
              <h3 className="font-heading font-bold text-sm mb-2 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-neutral-400" /> Répartition du CA
                <span className="ml-auto text-[10px] font-semibold text-[#0369A1]">{monthLabel(effMonthKey)}</span>
              </h3>
              {repTotal === 0 && <p className="text-[11px] text-neutral-400 py-6 text-center">Aucun encaissement sur l'année {sy?.annee}.</p>}
              {repTotal > 0 && (
                <>
                  <div className="relative h-36">
                    <SafeChart width="100%" height="100%">
                      <PieChart>
                        <Pie data={repServices} dataKey="amount" nameKey="label" innerRadius={42} outerRadius={60} paddingAngle={3} strokeWidth={0}>
                          {repServices.map((s) => <Cell key={s.service} fill={SERVICE_COLORS[s.service] || SERVICE_COLORS.autres} />)}
                        </Pie>
                        <Tooltip formatter={(v) => eur(v)} />
                      </PieChart>
                    </SafeChart>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <p className="font-heading text-sm font-bold" data-testid="acc-donut-total">{eur(repTotal)}</p>
                      <p className="text-[9px] text-neutral-400">CA total</p>
                    </div>
                  </div>
                  <div className="space-y-1 mt-2">
                    {repServices.map((s) => (
                      <div key={s.service} className="flex items-center gap-2 text-[11px]" data-testid={`acc-donut-${s.service}`}>
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: SERVICE_COLORS[s.service] || SERVICE_COLORS.autres }} />
                        <span className="flex-1 text-neutral-600">{s.label}</span>
                        <span className="font-semibold text-neutral-500">{Math.round(s.amount / repTotal * 100)} %</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Derniers paiements */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4" data-testid="acc-payments">
              <div className="flex items-center gap-2 mb-2">
                <Euro className="w-4 h-4 text-neutral-400" />
                <h3 className="font-heading font-bold text-sm flex-1">Derniers paiements</h3>
                <button onClick={() => { setTab("factures"); setStatusFilter("payee"); }} className="text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="acc-payments-all">
                  Voir tous ›
                </button>
              </div>
              {(dash?.derniers_paiements || []).length === 0 && <p className="text-[11px] text-neutral-400 py-3">Aucun paiement enregistré pour l'instant.</p>}
              {(dash?.derniers_paiements || []).map((p, i) => (
                <button key={`${p.invoice_id}-${i}`} onClick={() => { setTab("factures"); setDetailId(p.invoice_id); }} data-testid={`acc-payment-${p.invoice_id}-${i}`}
                  className="w-full flex items-center gap-2.5 py-2 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 -mx-1 px-1 rounded-lg transition-colors text-left">
                  <span className={`w-7 h-7 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0 ${avatarCls(p.client_name)}`}>{initialsOf(p.client_name)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[11px] font-semibold text-[#0F172A] truncate">{p.client_name}</span>
                    <span className="block text-[9px] text-neutral-400">Facture {p.number || "—"}</span>
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block text-[11px] font-bold text-emerald-600">+ {eur(p.amount)}</span>
                    <span className="block text-[9px] text-neutral-400">{relTime(p.date)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modales (existantes, inchangées) */}
      <InvoiceModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} clients={clients} invoice={editing} />
      <InvoiceDetail invoiceId={detailId} onClose={() => setDetailId(null)} onChanged={load}
        onEdit={(inv) => { setDetailId(null); setEditing(inv); setModalOpen(true); }} />
      <QuoteCreateModal open={quoteModalOpen || !!editQuote} quote={editQuote}
        onClose={() => { setQuoteModalOpen(false); setEditQuote(null); }} onSaved={load} clients={clients} />
      <QuoteImportModal open={importQuoteOpen} onClose={() => setImportQuoteOpen(false)} onSaved={load} clients={clients} />
      <QuoteDetailDrawer quoteId={quoteDetailId} onClose={() => setQuoteDetailId(null)} onChanged={load}
        onEdit={(qu) => { setQuoteDetailId(null); setEditQuote(qu); }}
        onSend={(qu) => { setQuoteDetailId(null); setSendQuote(qu); }}
        onOpenInvoice={(id) => { setQuoteDetailId(null); setTab("factures"); setDetailId(id); }} />
      <SendDocModal doc={sendQuote} kind="quote" onClose={() => setSendQuote(null)} onSent={load} />
      <SendDocModal doc={sendCn} kind="credit_note" onClose={() => setSendCn(null)} onSent={load} />
      <NewCreditNoteModal open={cnModal} onClose={() => setCnModal(false)} invoices={invoices} onSaved={load} />
    </div>
  );
}

/* ================= Pilotage (ancien dashboard, conservé) ================= */
function Pilotage({ dash, onOpenInvoice, remind, reminding, onSeeAllAuto }) {
  return (
    <div className="space-y-6 pb-6">
      <AutoBillingPanel variant="compact" onSeeAll={onSeeAllAuto} />

      {(dash.a_relancer || []).length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl overflow-hidden" data-testid="relancer-list">
          <p className="px-4 py-3 text-sm font-semibold text-orange-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Factures à relancer (envoyées il y a plus de 7 jours)
          </p>
          {dash.a_relancer.map((inv) => (
            <button key={inv.id} onClick={() => onOpenInvoice(inv.id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm border-t border-orange-100 hover:bg-orange-100/50 transition-colors text-left"
              data-testid={`relancer-${inv.id}`}>
              <span>
                <strong>{inv.number}</strong> · {inv.client_name} · envoyée il y a {inv.days} j
                {inv.last_reminded_at && (
                  <span className="block text-xs text-orange-700/70" data-testid={`relancer-last-${inv.id}`}>
                    Dernière relance le {inv.last_reminded_at.slice(8, 10)}/{inv.last_reminded_at.slice(5, 7)}/{inv.last_reminded_at.slice(0, 4)}
                    {inv.reminder_count > 1 ? ` (${inv.reminder_count} relances)` : ""}
                  </span>
                )}
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="font-medium text-orange-700">{eur(inv.reste)}</span>
                <span role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); if (reminding !== inv.id) remind(inv.id); }}
                  className="flex items-center gap-1.5 bg-orange-600 text-white rounded-full px-3 py-1.5 text-xs font-medium hover:bg-orange-700 transition-colors cursor-pointer"
                  data-testid={`relancer-send-${inv.id}`}>
                  <Send className="w-3.5 h-3.5" /> {reminding === inv.id ? "Envoi…" : "Relancer"}
                </span>
              </span>
            </button>
          ))}
          <p className="px-4 py-2.5 text-xs text-orange-700/70 border-t border-orange-100">
            Un email de relance automatique est envoyé au client à J+7 — ou relancez manuellement : le client reçoit uniquement sa facture en PDF, sans copie.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-neutral-900 text-white rounded-xl p-5 shadow-sm" data-testid="ca-encaisse-annee">
          <p className="text-[11px] uppercase tracking-wider text-white/50">Chiffre d'affaires encaissé {dash.annee}</p>
          <p className="font-heading font-bold text-3xl mt-1">{eur(dash.encaisse_annee)}</p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="ca-facture-annee">
          <p className="text-[11px] uppercase tracking-wider text-neutral-400">Chiffre d'affaires facturé {dash.annee}</p>
          <p className="font-heading font-bold text-3xl mt-1">{eur(dash.facture_annee)}</p>
          <p className="text-xs text-neutral-400 mt-0.5">Facturé ≠ encaissé : seuls les paiements reçus comptent dans l'encaissé.</p>
        </div>
      </div>

      {dash.objectif_annuel > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="objectif-annuel">
          <div className="flex justify-between items-baseline">
            <p className="text-sm font-semibold">Objectif annuel</p>
            <p className="text-sm text-neutral-500">{eur(dash.encaisse_annee)} / {eur(dash.objectif_annuel)}</p>
          </div>
          <div className="h-3 bg-neutral-100 rounded-full overflow-hidden mt-2">
            <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${Math.min(100, dash.encaisse_annee / dash.objectif_annuel * 100)}%` }} />
          </div>
          <div className="flex justify-between text-xs text-neutral-400 mt-1.5">
            <span data-testid="objectif-pct">{Math.round(dash.encaisse_annee / dash.objectif_annuel * 100)} %</span>
            <span>Reste à générer : {eur(Math.max(0, dash.objectif_annuel - dash.encaisse_annee))}</span>
          </div>
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="chart-monthly">
        <p className="text-sm font-semibold mb-1">Chiffre d'affaires par mois — depuis septembre 2026</p>
        <p className="text-xs text-neutral-400 mb-4">Encaissé (réel) vs facturé (émis). Mois sans donnée = 0 €.</p>
        <div className="h-56">
          <SafeChart width="100%" height="100%">
            <BarChart data={dash.months} margin={{ left: -20, right: 5, top: 5 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              {dash.objectif_mensuel > 0 && (
                <ReferenceLine y={dash.objectif_mensuel} stroke="#f97316" strokeDasharray="4 4"
                  label={{ value: "Objectif", fontSize: 10, fill: "#f97316" }} />
              )}
              <Bar dataKey="encaisse" name="Encaissé" fill="#16a34a" radius={[3, 3, 0, 0]} />
              <Bar dataKey="facture" name="Facturé" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
            </BarChart>
          </SafeChart>
        </div>
      </div>

      {dash.objectif_mensuel > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="objectif-mensuel">
          <p className="text-sm font-semibold mb-3">Objectif mensuel — {eur(dash.objectif_mensuel)}</p>
          <div className="flex flex-wrap gap-2">
            {dash.months.map((mo) => {
              const ok = mo.encaisse >= dash.objectif_mensuel;
              return (
                <div key={mo.key} data-testid={`obj-mois-${mo.key}`}
                  className={`border rounded-lg px-3 py-2 text-xs ${ok ? "bg-green-50 border-green-200 text-green-800" : "bg-neutral-50 border-neutral-200 text-neutral-500"}`}>
                  <p className="font-semibold">{mo.label}</p>
                  <p>{eur(mo.encaisse)} / {eur(dash.objectif_mensuel)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-3">
        <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="projection">
          <p className="text-sm font-semibold">Projection de CA à fin {dash.annee}</p>
          <p className="text-[11px] text-neutral-400 mb-3">Estimation — distincte du CA réellement encaissé.</p>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">CA encaissé</span><span>{eur(dash.projection.encaisse)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Récurrent restant estimé</span><span>{eur(dash.projection.recurrent_restant)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Devis acceptés à facturer</span><span>{eur(dash.projection.devis_acceptes)}</span></div>
            <div className="flex justify-between font-bold border-t border-neutral-100 pt-1.5">
              <span>Projection</span><span data-testid="projection-total">{eur(dash.projection.total)}</span>
            </div>
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="repartition">
          <p className="text-sm font-semibold mb-3">Répartition récurrent / ponctuel</p>
          <div className="space-y-1.5 text-sm mb-3">
            <div className="flex justify-between"><span className="text-neutral-500">Revenus récurrents</span><span>{eur(dash.repartition.recurrent)} / mois</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Revenus ponctuels</span><span>{eur(dash.repartition.ponctuel)} ce mois</span></div>
          </div>
          <div className="h-3 rounded-full overflow-hidden bg-neutral-100 flex">
            <div className="h-full bg-blue-500" style={{ width: `${dash.repartition.pct_recurrent}%` }} />
            <div className="h-full bg-neutral-300 flex-1" />
          </div>
          <p className="text-xs text-neutral-400 mt-1.5">
            {dash.repartition.pct_recurrent} % récurrent · {100 - dash.repartition.pct_recurrent} % ponctuel
          </p>
        </div>
      </div>

      {(dash.par_client || []).length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="ca-par-client">
          <p className="text-sm font-semibold mb-3">Répartition du CA encaissé par client ({dash.annee})</p>
          {dash.concentration_alerte && (
            <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-3" data-testid="concentration-alerte">
              {dash.concentration_alerte.pct} % de votre chiffre d'affaires dépend actuellement de {dash.concentration_alerte.client_name}.
            </p>
          )}
          <div className="space-y-2">
            {dash.par_client.map((c) => (
              <div key={c.client_id} className="flex items-center gap-3 text-sm" data-testid={`ca-client-${c.client_id}`}>
                <span className="w-40 truncate">{c.client_name}</span>
                <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full bg-neutral-700 rounded-full" style={{ width: `${c.pct}%` }} />
                </div>
                <span className="w-14 text-right text-neutral-500">{c.pct} %</span>
                <span className="w-24 text-right font-medium">{eur(c.encaisse)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
